import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { joistService } from '@/api'
import { useUIStore } from '@/stores/uiStore'
import { handleApiError } from '@/lib/apiErrorHandler'
import { leadKeys } from './useLeads'
import type {
  JoistImportResponse,
  JoistLeadFilterParams,
  JoistLeadListResponse,
  JoistPromoteAllResponse,
  JoistPromoteResponse,
  JoistScanRequest,
  JoistScanResponse,
} from '@/types/api'

export const joistKeys = {
  all: ['joist'] as const,
  scans: () => [...joistKeys.all, 'scan'] as const,
  scan: (id: string) => [...joistKeys.scans(), id] as const,
  lists: () => [...joistKeys.all, 'list'] as const,
  list: (filters: JoistLeadFilterParams) => [...joistKeys.lists(), filters] as const,
}

export function useJoistLeads(params?: JoistLeadFilterParams, enabled = true) {
  return useQuery<JoistLeadListResponse>({
    queryKey: joistKeys.list(params || {}),
    queryFn: () => joistService.getJoistLeads(params),
    enabled,
  })
}

export function useJoistScan(scanId: string | null) {
  return useQuery<JoistScanResponse>({
    queryKey: joistKeys.scan(scanId || ''),
    queryFn: () => joistService.getJoistScan(scanId as string),
    enabled: Boolean(scanId),
  })
}

export function useScanJoistExport() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation<JoistScanResponse, { detail?: string }, JoistScanRequest>({
    mutationFn: (data) => joistService.scanJoistExport(data),
    onSuccess: (data) => {
      queryClient.setQueryData(joistKeys.scan(data.scan_id), data)
      addToast({
        title: 'Scan complete',
        description:
          data.importable_rows === 1
            ? '1 lead ready to import'
            : `${data.importable_rows} leads ready to import`,
        variant: 'success',
      })
    },
    onError: (error) => {
      handleApiError(error, 'Failed to scan the Joist files')
    },
  })
}

/**
 * Promote a single Joist lead. The dialog re-queries afterwards rather than holding
 * a client-side list, so the server's promotion_status stays the source of truth.
 */
export function usePromoteJoistLead() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation<JoistPromoteResponse, { detail?: string }, string>({
    mutationFn: (id) => joistService.promoteJoistLead(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: joistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: data.skipped_duplicate ? 'Linked to existing lead' : 'Lead created',
        description: data.message,
        variant: 'success',
      })
    },
    onError: (error) => {
      handleApiError(error, 'Failed to promote the lead')
    },
  })
}

export function usePromoteAllJoistLeads() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation<JoistPromoteAllResponse, { detail?: string }, string | undefined>({
    mutationFn: (scanId) => joistService.promoteAllJoistLeads(scanId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: joistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() })
      addToast({
        title: 'Leads created',
        description: data.message,
        variant: data.failed > 0 ? 'warning' : 'success',
      })
    },
    onError: (error) => {
      handleApiError(error, 'Failed to promote the leads')
    },
  })
}

export function useImportJoistScan() {
  const queryClient = useQueryClient()
  const { addToast } = useUIStore()

  return useMutation<JoistImportResponse, { detail?: string }, string>({
    mutationFn: (scanId) => joistService.importJoistScan(scanId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: joistKeys.lists() })
      queryClient.invalidateQueries({ queryKey: joistKeys.scan(data.scan_id) })
      addToast({
        title: 'Import complete',
        description: data.message,
        variant: 'success',
      })
    },
    onError: (error) => {
      handleApiError(error, 'Failed to import the scanned leads')
    },
  })
}
